import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Radar, Trash2, Sparkles, Loader2, TrendingUp, Users, LayoutGrid, PlusCircle } from "lucide-react";

export default function Competitors() {
  const { selected } = useBrand();
  const navigate = useNavigate();
  const [comps, setComps] = useState([]);
  const [handle, setHandle] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [remixing, setRemixing] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [creatingIdx, setCreatingIdx] = useState(null);

  useEffect(() => { api.get("/competitors", { params: selected ? { brand: selected } : {} }).then((r) => setComps(r.data)); }, [selected]);

  const add = async () => {
    if (!handle.trim()) return;
    try {
      const { data } = await api.post("/competitors", { handle, platform, brand: selected || null });
      setComps([data, ...comps]);
      setHandle("");
      toast.success("Competitor tracked");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remix = async (c) => {
    setRemixing(c.id);
    try {
      const { data } = await api.post(`/competitors/${c.id}/remix`);
      setIdeas({ comp: c, list: data.ideas });
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setRemixing(null);
    }
  };

  const createPostFromIdea = async (idx) => {
    if (!ideas) return;
    setCreatingIdx(idx);
    const wait = toast.loading("Creating a draft post from this remix idea…");
    try {
      const { data } = await api.post(`/competitors/${ideas.comp.id}/create-post`, { idea_index: idx });
      toast.success("Draft post added to the Competitor Remixes batch — visual is rendering in the background.", {
        id: wait,
        duration: 10000,
        action: { label: "Open in Review", onClick: () => navigate(`/review?batch=${data.batch_id}`) },
      });
      setIdeas(null);
    } catch (e) {
      toast.error(errText(e), { id: wait });
    } finally {
      setCreatingIdx(null);
    }
  };

  return (
    <div className="space-y-8 fade-up" data-testid="competitors-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Competitor Intelligence{selected ? ` · ${selected}` : ""}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Learn from the field. Stay original.</h1>
      </div>

      <Card className="max-w-2xl">
        <CardContent className="p-5 flex flex-wrap gap-3">
          <Input data-testid="competitor-handle-input" className="flex-1 min-w-[180px]" placeholder="@competitor_handle" value={handle} onChange={(e) => setHandle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="w-40" data-testid="competitor-platform-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["instagram", "linkedin", "twitter", "facebook", "tiktok"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="add-competitor-btn" onClick={add}><Radar size={14} className="mr-2" /> Track</Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {comps.map((c) => (
          <Card key={c.id} data-testid={`competitor-card-${c.id}`} className="hover:-translate-y-1 hover:shadow-lg transition-transform duration-200">
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div>
                <CardTitle className="font-heading text-lg">@{c.handle}</CardTitle>
                <Badge variant="secondary" className="capitalize mt-1">{c.platform}</Badge>
              </div>
              <Button variant="ghost" size="icon" data-testid={`delete-competitor-${c.id}`} onClick={() => api.delete(`/competitors/${c.id}`).then(() => setComps(comps.filter((x) => x.id !== c.id)))}><Trash2 size={14} /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-muted">
                  <Users size={13} className="mx-auto mb-1 text-muted-foreground" />
                  <p className="font-heading font-semibold text-sm">{(c.metrics.followers / 1000).toFixed(1)}k</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Followers</p>
                </div>
                <div className="p-2 rounded-lg bg-muted">
                  <TrendingUp size={13} className="mx-auto mb-1 text-muted-foreground" />
                  <p className="font-heading font-semibold text-sm">{c.metrics.avg_engagement_rate}%</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Engagement</p>
                </div>
                <div className="p-2 rounded-lg bg-muted">
                  <LayoutGrid size={13} className="mx-auto mb-1 text-muted-foreground" />
                  <p className="font-heading font-semibold text-sm">{c.metrics.posts_per_week}/wk</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Posts</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Top format: <span className="font-semibold text-foreground capitalize">{c.metrics.top_format.replace("_", " ")}</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {c.metrics.top_hooks.map((h) => <Badge key={h} variant="outline" className="text-[10px]">{h}</Badge>)}
                </div>
              </div>
              <Button className="w-full" data-testid={`remix-btn-${c.id}`} disabled={remixing === c.id} onClick={() => remix(c)}>
                {remixing === c.id ? <Loader2 size={14} className="animate-spin" /> : <><Sparkles size={14} className="mr-2" /> Remix Strategy</>}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {comps.length === 0 && <p className="text-sm text-muted-foreground">Track a competitor handle to benchmark posting frequency, formats and engagement.</p>}

      <Dialog open={!!ideas} onOpenChange={(v) => !v && setIdeas(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="remix-dialog">
          {ideas && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Remixed from @{ideas.comp.handle}'s winning structure</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {ideas.list.map((idea, i) => (
                  <div key={i} className="p-4 rounded-xl border border-border space-y-2" data-testid={`remix-idea-${i}`}>
                    <div className="flex flex-wrap gap-2">
                      <Badge>{idea.hook_used}</Badge>
                      <Badge variant="secondary" className="capitalize">{idea.format}</Badge>
                    </div>
                    <p className="font-semibold">{idea.title}</p>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{idea.copy}</p>
                    <p className="text-xs text-accent">{(idea.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
                    {idea.why_it_works && <p className="text-xs text-muted-foreground italic">Why it works: {idea.why_it_works}</p>}
                    <div className="pt-2 flex justify-end">
                      <Button size="sm" data-testid={`remix-create-post-${i}`} disabled={creatingIdx !== null} onClick={() => createPostFromIdea(i)}>
                        {creatingIdx === i ? <Loader2 size={14} className="mr-2 animate-spin" /> : <PlusCircle size={14} className="mr-2" />} Create post from this idea
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
